"use client"

import { useState, useEffect, useRef } from "react"
import { X, Upload, AlertCircle, Info } from "lucide-react"
import { useDispatch } from "react-redux"
import { createWhatsappTempletesAction } from "../../../services/action/common"

export default function TempleteModal({ setShowCreateTemplateModal, templateToEdit, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    category: "MARKETING",
    language: "en_US",
    components: [
      {
        type: "HEADER",
        format: "TEXT",
        text: "",
      },
      {
        type: "BODY",
        text: "",
      },
      {
        type: "FOOTER",
        text: "",
      },
    ],
    status: "PENDING",
  })

  const [errors, setErrors] = useState({})
  const [headerImage, setHeaderImage] = useState(null)
  const fileInputRef = useRef(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const dispatch = useDispatch()

  // Add a function to handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, headerImage: "Image size must be less than 5MB" })
        return
      }

      setHeaderImage(file)
      setErrors({ ...errors, headerImage: undefined })

      // Update the header format to IMAGE
      handleComponentChange("HEADER", "format", "IMAGE")
    }
  }

  // If editing an existing template, populate the form
  useEffect(() => {
    if (templateToEdit) {
      setFormData({
        ...templateToEdit,
        // Ensure all required components exist
        components: templateToEdit.components || [
          {
            type: "HEADER",
            format: "TEXT",
            text: "",
          },
          {
            type: "BODY",
            text: "",
          },
          {
            type: "FOOTER",
            text: "",
          },
        ],
      })

      // If there's a header image in the template, set it
      if (templateToEdit.headerImage) {
        setHeaderImage(templateToEdit.headerImage)
      }
    }
  }, [templateToEdit])

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData({
      ...formData,
      [name]: value,
    })

    // Clear error when user types
    if (errors[name]) {
      setErrors({ ...errors, [name]: undefined })
    }
  }

  const handleComponentChange = (type, field, value) => {
    setFormData({
      ...formData,
      components: formData.components.map((component) => {
        if (component.type === type) {
          return {
            ...component,
            [field]: value,
          }
        }
        return component
      }),
    })

    // Clear error when user types in a component
    if (type === "BODY" && field === "text" && errors.bodyText) {
      setErrors({ ...errors, bodyText: undefined })
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Validate name
    if (!formData.name.trim()) {
      newErrors.name = "Template name is required"
    }

    // Validate body text
    const bodyComponent = formData.components.find((c) => c.type === "BODY")
    if (!bodyComponent || !bodyComponent.text.trim()) {
      newErrors.bodyText = "Body text is required"
    }

    // Validate header image if format is IMAGE
    const headerComponent = formData.components.find((c) => c.type === "HEADER")
    if (headerComponent && headerComponent.format === "IMAGE" && !headerImage) {
      newErrors.headerImage = "Header image is required for image format"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)

    // Filter out empty components
    const filteredComponents = formData.components.filter((component) => {
      // Keep header component if it has an image, even if text is empty
      if (component.type === "HEADER" && component.format === "IMAGE" && headerImage) {
        return true
      }
      return component.text && component.text.trim() !== ""
    })

    const submitData = {
      ...formData,
      components: filteredComponents,
    }

    // Add the header image if it exists
    if (headerImage) {
      submitData.headerImage = headerImage
    }

    dispatch(
      createWhatsappTempletesAction({
        data: submitData,
        cb: () => {
          setIsSubmitting(false)
          setShowCreateTemplateModal(false)
          if (onClose) onClose()
        },
        errorCb: (error) => {
          setIsSubmitting(false)
          console.error("Error creating template:", error)
          setErrors({ ...errors, submit: error.message || "Failed to create template" })
        },
      }),
    )
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-[fadeIn_0.3s_ease-in-out]">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {templateToEdit ? "Edit Template" : "Create New Template"}
            </h2>
            <button
              onClick={() => {
                setShowCreateTemplateModal(false)
                if (onClose) onClose()
              }}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            {templateToEdit
              ? "Update your WhatsApp template details below."
              : "Fill in the details to create a new WhatsApp template."}
          </p>

          {errors.submit && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium">Error submitting template</p>
                <p className="text-sm">{errors.submit}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Template Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter template name"
                  className={`w-full px-3 py-2.5 border ${errors.name ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-2 focus:ring-[#001B3D] focus:border-transparent transition-colors`}
                />
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <AlertCircle className="w-3 h-3 mr-1" /> {errors.name}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <label htmlFor="category" className="block text-sm font-medium text-gray-700">
                  Category
                </label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#001B3D] focus:border-transparent transition-colors"
                >
                  <option value="MARKETING">Marketing</option>
                  <option value="UTILITY">Utility</option>
                  <option value="AUTHENTICATION">Authentication</option>
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <label htmlFor="language" className="block text-sm font-medium text-gray-700">
                Language
              </label>
              <select
                id="language"
                name="language"
                value={formData.language}
                onChange={handleInputChange}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#001B3D] focus:border-transparent transition-colors"
              >
                <option value="en_US">English (US)</option>
                <option value="en_GB">English (UK)</option>
                <option value="hi_IN">Hindi</option>
                <option value="es_ES">Spanish</option>
                <option value="fr_FR">French</option>
              </select>
            </div>

            <div className="space-y-4">
              <h3 className="font-medium text-gray-900">Template Content</h3>

              {/* Header */}
              <div className="border rounded-md p-4 space-y-3 shadow-sm">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-gray-700">Header (Optional)</h4>
                </div>
                <div className="space-y-2">
                  <label htmlFor="headerFormat" className="block text-sm font-medium text-gray-700">
                    Format
                  </label>
                  <select
                    id="headerFormat"
                    value={formData.components.find((c) => c.type === "HEADER")?.format || "TEXT"}
                    onChange={(e) => handleComponentChange("HEADER", "format", e.target.value)}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#001B3D] focus:border-transparent transition-colors"
                  >
                    <option value="TEXT">Text</option>
                    <option value="IMAGE">Image</option>
                    <option value="VIDEO">Video</option>
                    <option value="DOCUMENT">Document</option>
                  </select>
                </div>

                {formData.components.find((c) => c.type === "HEADER")?.format === "IMAGE" ? (
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">Header Image</label>
                    <div
                      className={`border-2 border-dashed ${errors.headerImage ? "border-red-300" : "border-gray-300"} rounded-lg p-4 hover:border-[#001B3D] transition-colors`}
                    >
                      <div className="flex flex-col items-center justify-center">
                        {headerImage ? (
                          <div className="space-y-2 w-full">
                            <div className="relative w-full max-w-md mx-auto">
                              <img
                                src={typeof headerImage === "string" ? headerImage : URL.createObjectURL(headerImage)}
                                alt="Header preview"
                                className="w-full h-auto rounded-lg shadow-md"
                              />
                              <button
                                type="button"
                                onClick={() => setHeaderImage(null)}
                                className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-md hover:bg-gray-100 transition-colors"
                              >
                                <X className="w-4 h-4 text-gray-700" />
                              </button>
                            </div>
                            <p className="text-sm text-gray-500 text-center">
                              {typeof headerImage === "string" ? "Current header image" : headerImage.name}
                            </p>
                          </div>
                        ) : (
                          <>
                            <Upload className="w-10 h-10 text-gray-400 mb-2" />
                            <p className="text-sm font-medium text-gray-700 mb-1">Drag and drop your image here</p>
                            <p className="text-xs text-gray-500 mb-2">or click to browse files</p>
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="px-3 py-1.5 bg-[#001B3D] text-white text-sm rounded-md hover:bg-[#002857] transition-all duration-200 shadow-sm hover:shadow"
                            >
                              Select Image
                            </button>
                            <input
                              ref={fileInputRef}
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                              className="hidden"
                            />
                          </>
                        )}
                      </div>
                    </div>
                    {errors.headerImage ? (
                      <p className="text-red-500 text-xs mt-1 flex items-center">
                        <AlertCircle className="w-3 h-3 mr-1" /> {errors.headerImage}
                      </p>
                    ) : (
                      <p className="text-xs text-gray-500">
                        Recommended image size: 800x418 pixels. Maximum file size: 5MB.
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <label htmlFor="headerText" className="block text-sm font-medium text-gray-700">
                      Text
                    </label>
                    <input
                      id="headerText"
                      type="text"
                      value={formData.components.find((c) => c.type === "HEADER")?.text || ""}
                      onChange={(e) => handleComponentChange("HEADER", "text", e.target.value)}
                      placeholder="Enter header text"
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#001B3D] focus:border-transparent transition-colors"
                    />
                  </div>
                )}
              </div>

              {/* Body */}
              <div className="border rounded-md p-4 space-y-3 shadow-sm">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-gray-700">Body (Required)</h4>
                </div>
                <div className="space-y-2">
                  <label htmlFor="bodyText" className="block text-sm font-medium text-gray-700">
                    Text
                  </label>
                  <textarea
                    id="bodyText"
                    value={formData.components.find((c) => c.type === "BODY")?.text || ""}
                    onChange={(e) => handleComponentChange("BODY", "text", e.target.value)}
                    placeholder="Enter body text. Use {{1}}, {{2}}, etc. for variables."
                    rows={5}
                    className={`w-full px-3 py-2.5 border ${errors.bodyText ? "border-red-500" : "border-gray-300"} rounded-md focus:outline-none focus:ring-2 focus:ring-[#001B3D] focus:border-transparent transition-colors`}
                  />
                  {errors.bodyText && (
                    <p className="text-red-500 text-xs mt-1 flex items-center">
                      <AlertCircle className="w-3 h-3 mr-1" /> {errors.bodyText}
                    </p>
                  )}
                  <div className="flex items-start gap-2 bg-blue-50 p-2 rounded-md">
                    <Info className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-blue-700">
                      Use {"{"}
                      {"{"}
                      {1}
                      {"}"}
                      {"}"}, {"{"}
                      {"{"}
                      {2}
                      {"}"}
                      {"}"}, etc. for variables. Use *text* for bold text.
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border rounded-md p-4 space-y-3 shadow-sm">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-gray-700">Footer (Optional)</h4>
                </div>
                <div className="space-y-2">
                  <label htmlFor="footerText" className="block text-sm font-medium text-gray-700">
                    Text
                  </label>
                  <input
                    id="footerText"
                    type="text"
                    value={formData.components.find((c) => c.type === "FOOTER")?.text || ""}
                    onChange={(e) => handleComponentChange("FOOTER", "text", e.target.value)}
                    placeholder="Enter footer text"
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#001B3D] focus:border-transparent transition-colors"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowCreateTemplateModal(false)
                  if (onClose) onClose()
                }}
                className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-all duration-200"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2.5 bg-[#001B3D] text-white rounded-md hover:bg-[#002857] transition-all duration-200 shadow-sm hover:shadow flex items-center justify-center min-w-[120px]"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></div>
                    <span>{templateToEdit ? "Updating..." : "Creating..."}</span>
                  </>
                ) : (
                  <span>{templateToEdit ? "Update Template" : "Create Template"}</span>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

